import { Fragment, ReactNode, forwardRef } from 'react';
import styled from 'styled-components';
import { DoubleArrow } from 'ui/icons/navigation/DoubleArrow';
import { DownArrow } from 'ui/icons/navigation/DownArrow';
import { UpArrow } from 'ui/icons/navigation/UpArrow';
import { button, colors, spacing } from 'ui/theme';

import { ASCENDING, DESCENDING, SortDirections } from 'constants/sortDirections';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { AlignTypes } from 'ui/tableHeader/constants';

type HeaderCellProps = {
  isActiveSort?: boolean;
  sortable: boolean;
  isSticky: boolean;
  width?: number;
};
export const HeaderCell = styled.div<HeaderCellProps>`
  display: table-cell;
  ${button};
  color: ${({ isActiveSort, sortable }) =>
    sortable && isActiveSort ? colors.textLink : colors.textPrimary};
  white-space: nowrap;
  padding: ${spacing.half};
  cursor: ${({ sortable }) => (sortable ? 'pointer' : 'inherit')};
  ${({ isSticky }) =>
    isSticky &&
    `position: sticky;
    top: 0;
  z-index: 1;
  background: inherit;`}
  ${({ width }) => (width ? `width : ${width}px` : '')};
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: ${({ align }: { align: AlignTypes }) => align};
  align-items: center;
`;

const StyledUp = styled(UpArrow)`
  path {
    fill: ${colors.textLink};
  }
`;

const StyledDown = styled(DownArrow)`
  path {
    fill: ${colors.textLink};
  }
`;

type Props = {
  columnData: {
    align: AlignTypes;
    name: string | JSX.Element;
    value?: string;
    keepCapitalization?: boolean;
  };
  isActiveSort?: boolean;
  handleClick: () => void;
  sortable?: boolean;
  sortDirection?: SortDirections;
  children?: ReactNode;
  isSticky?: boolean;
  width?: number;
};

export const TableHeaderCell = forwardRef<HTMLDivElement, Props>(
  (
    {
      columnData,
      handleClick,
      isActiveSort,
      sortable = true,
      sortDirection,
      children,
      isSticky = false,
      width,
      ...props
    }: Props,
    ref,
  ) => {
    let icon = <DoubleArrow />;
    if (isActiveSort) {
      if (sortDirection === ASCENDING || sortDirection === SORT_DIRECTIONS.ASCENDING) {
        icon = <StyledUp />;
      }
      if (sortDirection === DESCENDING || sortDirection === SORT_DIRECTIONS.DESCENDING) {
        icon = <StyledDown />;
      }
    }

    return (
      <HeaderCell
        // @ts-ignore
        data-test={props['data-test'] ? props['data-test'] : 'header-cell'}
        id={`header-cell-${columnData.value}`}
        isActiveSort={isActiveSort}
        onClick={handleClick}
        sortable={sortable}
        role="columnheader"
        isSticky={isSticky}
        width={width}
        ref={ref}
        {...props}
      >
        <ContentWrapper align={columnData.align}>
          {children || (
            <Fragment>
              {columnData.name}
              {sortable && icon}
            </Fragment>
          )}
        </ContentWrapper>
      </HeaderCell>
    );
  },
);

export default TableHeaderCell;
